class CartUtils {
  static subscriptionAttributesTemplate = {
    order_type: '',
    pricingVariantId: '',
    membershipPlan: '',
    boxName: '',
    fineWinesCount: '',
    quizToken: '',
    state_code: '',
    deviceId: '',
    totalRedWines: '',
    totalWhiteWines: '',
    totalRoseWines: '',
    totalSparklingWines: '',
    allowedVariantIds: '',
  };

  static normalizeVariantId(value) {
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';

    if (rawValue.startsWith('gid://shopify/ProductVariant/')) {
      return rawValue;
    }

    if (/^\d+$/.test(rawValue)) {
      return `gid://shopify/ProductVariant/${rawValue}`;
    }

    return rawValue;
  }

  static getCartAttributes(cartData) {
    return cartData?.attributes || {};
  }

  static isIntroCart(cartData) {
    const attrs = this.getCartAttributes(cartData);
    return attrs.order_type === 'intro_club_order';
  }

  static hasSubscriptionAttributes(cartData) {
    return this.isIntroCart(cartData);
  }

  static hasIntroCartAttributes(cartData) {
    return this.isIntroCart(cartData);
  }

  static mergeCartAttributes(attributes = {}, { clearIntro = false } = {}) {
    const merged = { ...(attributes || {}) };

    if (clearIntro) {
      Object.assign(merged, this.subscriptionAttributesTemplate);
    }

    return merged;
  }

  static getCookieValue(name) {
    if (typeof document === 'undefined' || !document.cookie) return '';

    const prefix = `${name}=`;
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    return cookie ? cookie.slice(prefix.length) : '';
  }

  static setCookieValue(name, value) {
    if (typeof document === 'undefined') return;

    document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
  }

  static stripCartCookieKey() {
    const cartCookieNames = ['cart', '_shopify_cart'];

    for (const cookieName of cartCookieNames) {
      const currentValue = this.getCookieValue(cookieName);
      if (!currentValue || !currentValue.includes('?key=')) continue;

      const strippedValue = currentValue.split('?key=')[0];
      this.setCookieValue(cookieName, strippedValue);
      console.info('[CartUtils] fresh cart token requested after intro exit', {
        cookieName,
      });
      return true;
    }

    return false;
  }

  static dispatchCartEvent(eventName = 'cart:updated') {
    document.dispatchEvent(new CustomEvent(eventName));
  }

  static buildIntroExitUpdates(cartData) {
    const attrs = this.getCartAttributes(cartData);
    const pricingVariantId = this.normalizeVariantId(attrs.pricingVariantId);
    const updates = {};

    (cartData?.items || []).forEach((item) => {
      const itemVariantId = this.normalizeVariantId(item.variant_id);

      if (
        item.product_type === 'membership' ||
        (pricingVariantId && itemVariantId === pricingVariantId)
      ) {
        updates[item.key] = 0;
      }
    });

    return updates;
  }

  static normalizeReplayItems(items = []) {
    const itemsByVariantId = new Map();

    (Array.isArray(items) ? items : [items]).forEach((item) => {
      const rawId = item?.id ?? item?.variantId ?? "";
      const idKey = String(rawId).trim();
      const quantity = Number(item?.quantity);

      if (!idKey || !Number.isInteger(quantity) || quantity <= 0) {
        return;
      }

      const existing = itemsByVariantId.get(idKey);
      const normalizedId =
        typeof rawId === 'number'
          ? rawId
          : /^\d+$/.test(idKey)
            ? Number(idKey)
            : idKey;

      itemsByVariantId.set(idKey, {
        id: existing?.id ?? normalizedId,
        quantity: (existing?.quantity || 0) + quantity,
      });
    });

    return Array.from(itemsByVariantId.values());
  }

  static buildReplayItemsFromCart(cartData, updates = {}) {
    const attrs = this.getCartAttributes(cartData);
    const pricingVariantId = this.normalizeVariantId(attrs.pricingVariantId);
    const replayItems = [];

    (cartData?.items || []).forEach((item) => {
      const normalizedVariantId = this.normalizeVariantId(item.variant_id);

      if (
        item.product_type === 'membership' ||
        normalizedVariantId === pricingVariantId
      ) {
        return;
      }

      const nextQuantity = Object.prototype.hasOwnProperty.call(updates, item.key)
        ? Number(updates[item.key])
        : Number(item.quantity || 0);

      if (!Number.isInteger(nextQuantity) || nextQuantity <= 0) {
        return;
      }

      replayItems.push({
        id: item.variant_id,
        quantity: nextQuantity,
      });
    });

    return this.normalizeReplayItems(replayItems);
  }

  static async replayIntoFreshCart(items, attributes = {}) {
    const payloadItems = this.normalizeReplayItems(items);
    const payloadAttributes = this.mergeCartAttributes(attributes, {
      clearIntro: true,
    });

    // Drop every line + attribute from the persisted cart so the membership
    // line and intro contract cannot leak into the ad hoc flow. /cart/clear.js
    // wipes items, attributes, and note in one round trip.
    await fetch('/cart/clear.js', { method: 'POST' });

    this.stripCartCookieKey();

    if (payloadItems.length === 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {},
          attributes: payloadAttributes,
        }),
      });

      return this.fetchCart();
    }

    const res = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: payloadItems,
        attributes: payloadAttributes,
      }),
    });

    if (res.status === 422) {
      const { errors } = await res.json();
      const errorMessage = Array.isArray(errors)
        ? errors.join(', ')
        : 'Unable to rebuild cart after intro exit.';

      throw new Error(errorMessage);
    }

    if (!res.ok) {
      throw new Error('Failed to rebuild cart after intro exit');
    }

    return this.fetchCart();
  }

  static async addToCart(items, options = {}) {
    try {
      const cartBefore = await this.fetchCart();
      const shouldExitIntroMode = this.isIntroCart(cartBefore);

      if (shouldExitIntroMode) {
        console.info('[CartUtils] theme intro-to-ad hoc cart reset started', {
          action: 'add',
        });
        const rebuiltCart = await this.replayIntoFreshCart(
          Array.isArray(items) ? items : [items],
          options.attributes || {},
        );

        this.dispatchCartEvent();

        if (window.CartCountUpdater) {
          window.CartCountUpdater.updateCartIcon(rebuiltCart);
        }

        return rebuiltCart;
      }

      const body = {
        items: Array.isArray(items) ? items : [items],
      };

      const shouldClearIntroAttributes = options.clearAttributes === true;
      if (shouldClearIntroAttributes || options.attributes) {
        body.attributes = this.mergeCartAttributes(options.attributes || {}, {
          clearIntro: shouldClearIntroAttributes,
        });
      }

      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (res.status === 422) {
        const { errors } = await res.json();
        const errorMessage = Array.isArray(errors)
          ? errors.join(', ')
          : 'Unable to add item to cart. Please try again.';

        throw new Error(errorMessage);
      }

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.message || 'Add to cart failed');
      }

      const cart = await this.fetchCart();

      this.dispatchCartEvent();

      if (window.CartCountUpdater) {
        window.CartCountUpdater.updateCartIcon(cart);
      }

      return cart;
    } catch (error) {
      console.error('[CartUtils.addToCart] Fatal error:', error);
      throw error;
    }
  }

  static async updateCart(updates, options = {}) {
    try {
      const cartBefore = await this.fetchCart();
      const shouldExitIntroMode = this.isIntroCart(cartBefore) && Object.keys(updates || {}).length > 0;

      if (shouldExitIntroMode) {
        console.info('[CartUtils] theme intro-to-ad hoc cart reset started', {
          action: 'update',
        });
        const rebuiltCart = await this.replayIntoFreshCart(
          this.buildReplayItemsFromCart(cartBefore, updates),
          options.attributes || {},
        );

        this.dispatchCartEvent();

        if (window.CartCountUpdater) {
          window.CartCountUpdater.updateCartIcon(rebuiltCart);
        }

        return rebuiltCart;
      }

      const body = {
        updates,
      };

      const shouldClearIntroAttributes = shouldExitIntroMode || options.clearAttributes === true;
      if (shouldClearIntroAttributes || options.attributes) {
        body.attributes = this.mergeCartAttributes(options.attributes || {}, {
          clearIntro: shouldClearIntroAttributes,
        });
      }

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.description || errData?.message || 'Failed to update cart');
      }

      const latestCart = await this.fetchCart();

      this.dispatchCartEvent();

      if (window.CartCountUpdater) {
        window.CartCountUpdater.updateCartIcon(latestCart);
      }

      return latestCart;
    } catch (error) {
      console.error('Error in updateCart:', error);
      throw error;
    }
  }

  static async removeSubscriptionProducts(cartData) {
    try {
      const updates = this.buildIntroExitUpdates(cartData);

      if (Object.keys(updates).length === 0) {
        if (this.isIntroCart(cartData)) {
          await this.clearIntroCartAttributes();
        }
        return;
      }

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          attributes: this.mergeCartAttributes({}, { clearIntro: true }),
        }),
      });

      if (!res.ok) throw new Error('Failed to remove subscription products');
      await res.json();
    } catch (error) {
      console.error('Error in removeSubscriptionProducts:', error);
    }
  }

  static async clearIntroCartAttributes() {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: this.mergeCartAttributes({}, { clearIntro: true }),
      }),
    });
  }

  static async clearSubscriptionAttributesIfNoSubsExist() {
    try {
      const cart = await this.fetchCart();
      const hasMembership = cart.items?.some((item) => item.product_type === 'membership');

      if (!hasMembership) {
        await this.clearIntroCartAttributes();
      }
    } catch (error) {
      console.error('Error in clearSubscriptionAttributesIfNoSubsExist:', error);
    }
  }

  static async fetchCart() {
    const res = await fetch('/cart.js', { cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }
}

window.CartUtils = CartUtils;
